// M12.5 — trend section on /app/report. Controls row (platform filter +
// metric), accumulation on the client via d3-array `rollup`, renders into
// <TimeSeriesChart>. The chart itself is SVG with hover tooltips; the PDF
// builds a static equivalent via the shared `buildTimeSeriesSvg` helper.
//
// M13.1 — granularity lifted out into a page-level <GranularityControl>
// so M13.x sections can share a single user-controlled granularity. The
// smart default rule (≤30 days = day, 31-120 = week, >120 = month) lives
// in GranularityControl.tsx as `inferGranularity()`. This component now
// just consumes the resolved granularity as a prop.

import { useMemo, useState, type ReactNode } from "react";
import { rollup } from "d3-array";
import { timeMonday, timeMonth } from "d3-time";
import type { CanonicalPlatform, ReportPayload } from "@/lib/api";
import { SectionHeader } from "@/components/report/SectionHeader";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { PLATFORM_COLOR_HEX } from "./platform-colors";
import type { Granularity } from "./GranularityControl";

type TrendMetric = "sessions" | "keyEvents" | "customConversions" | "totalRevenue";

interface TimeSeriesSectionProps {
  trend: ReportPayload["trend"];
  /** When true, the custom-conversions metric option renders. */
  customConversionsConfigured: boolean;
  /** When true, the revenue metric option renders. */
  showRevenue: boolean;
  /** M13.1 — owned by the page so M13.x sections can share it. */
  granularity: Granularity;
}

const ALL_KEY = "__all__";
const ALL_COLOR = "#9583FF"; // purple-500 — matches section eyebrow + PDF default

export function TimeSeriesSection({
  trend,
  customConversionsConfigured,
  showRevenue,
  granularity,
}: TimeSeriesSectionProps) {
  const platformsPresent = useMemo(() => {
    const set = new Set<CanonicalPlatform>();
    for (const row of trend) set.add(row.platform);
    // Keep the order stable — descending session totals reads better
    // than alphabetical, mirrors the leaderboard.
    const totals = new Map<CanonicalPlatform, number>();
    for (const row of trend)
      totals.set(row.platform, (totals.get(row.platform) ?? 0) + row.sessions);
    return Array.from(set).sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
  }, [trend]);

  const [metric, setMetric] = useState<TrendMetric>("sessions");
  // Selection state: a Set that may contain `ALL_KEY` (renders an
  // aggregate "All social" line), individual platforms (one series each),
  // or both at once. Default to "All" so the chart reads as a single
  // purple line at first glance; the user can then add per-platform
  // lines on top, or switch to a platform-only view.
  const [selected, setSelected] = useState<Set<CanonicalPlatform | typeof ALL_KEY>>(
    new Set([ALL_KEY]),
  );

  const series = useMemo(() => {
    return buildSeries(trend, selected, metric, granularity, platformsPresent);
  }, [trend, selected, metric, granularity, platformsPresent]);

  // Toggle a single platform in/out of the selection. "All" is a peer
  // of the platforms now — selecting a platform doesn't clear "All",
  // and vice versa, so users can overlay the aggregate line with one
  // or more platform lines simultaneously. Empty-state guard restores
  // "All" so the chart never renders zero series.
  const togglePlatform = (p: CanonicalPlatform) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      if (next.size === 0) next.add(ALL_KEY);
      return next;
    });
  };

  // Toggle the aggregate "All" series in the same way — independent of
  // any platform selections. Same empty-state guard applies.
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ALL_KEY)) next.delete(ALL_KEY);
      else next.add(ALL_KEY);
      if (next.size === 0) next.add(ALL_KEY);
      return next;
    });
  };

  const allActive = selected.has(ALL_KEY);
  const metricLabel = METRIC_LABELS[metric];

  return (
    <section id="trend" className="scroll-mt-24 space-y-4">
      <SectionHeader
        eyebrow="Social traffic trends"
        title={
          <>
            How social traffic <em className="font-serif text-purple-700">changes</em> over time
          </>
        }
        description="Per-platform volume over the window. Toggle platforms in or out below; metric and granularity controls let you compare sessions, key events, and the rest at day / week / month resolution."
      />

      <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
        <ControlsRow
          platforms={platformsPresent}
          selected={selected}
          allActive={allActive}
          onToggle={togglePlatform}
          onToggleAll={toggleAll}
          metric={metric}
          onMetricChange={setMetric}
          customConversionsConfigured={customConversionsConfigured}
          showRevenue={showRevenue}
        />
        <div className="mt-4">
          <TimeSeriesChart series={series} yLabel={metricLabel} granularity={granularity} />
        </div>
      </div>
    </section>
  );
}

const METRIC_LABELS: Record<TrendMetric, string> = {
  sessions: "Sessions",
  keyEvents: "Key events",
  customConversions: "Custom conversions",
  totalRevenue: "Revenue",
};

interface ControlsRowProps {
  platforms: CanonicalPlatform[];
  selected: Set<CanonicalPlatform | typeof ALL_KEY>;
  allActive: boolean;
  onToggle: (p: CanonicalPlatform) => void;
  onToggleAll: () => void;
  metric: TrendMetric;
  onMetricChange: (m: TrendMetric) => void;
  customConversionsConfigured: boolean;
  showRevenue: boolean;
}

function ControlsRow({
  platforms,
  selected,
  allActive,
  onToggle,
  onToggleAll,
  metric,
  onMetricChange,
  customConversionsConfigured,
  showRevenue,
}: ControlsRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Platform chips. "All" is a peer of the platforms — toggling it
       *  in/out adds or removes the aggregate "All social" line without
       *  affecting the platform lines, so the user can overlay a totals
       *  line on top of selected platforms. */}
      <div className="flex flex-wrap items-center gap-2">
        <ChipButton active={allActive} onClick={onToggleAll} colorDot={ALL_COLOR}>
          All
        </ChipButton>
        {platforms.map((p) => (
          <ChipButton
            key={p}
            active={selected.has(p)}
            onClick={() => onToggle(p)}
            colorDot={PLATFORM_COLOR_HEX[p]}
          >
            {p}
          </ChipButton>
        ))}
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-3">
        {/* Metric dropdown — granularity now lives on the page-level
            <GranularityControl> next to the section header so M13.x
            sections share a single user-controlled value. */}
        <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700">
          <span className="uppercase tracking-wide text-neutral-500">Metric</span>
          <select
            value={metric}
            onChange={(e) => onMetricChange(e.target.value as TrendMetric)}
            className="rounded-lg border border-neutral-200 bg-white px-2 py-1 text-sm font-medium text-neutral-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            <option value="sessions">Sessions</option>
            <option value="keyEvents">Key events</option>
            {customConversionsConfigured ? (
              <option value="customConversions">Custom conversions</option>
            ) : null}
            {showRevenue ? <option value="totalRevenue">Revenue</option> : null}
          </select>
        </label>
      </div>
    </div>
  );
}

interface ChipButtonProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  colorDot?: string;
}

function ChipButton({ active, onClick, children, colorDot }: ChipButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-colors ${
        active
          ? "bg-purple-50 text-purple-700 ring-purple-300"
          : "bg-white text-neutral-700 ring-neutral-200 hover:bg-neutral-50"
      }`}
    >
      {colorDot ? (
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: colorDot }}
        />
      ) : null}
      {children}
    </button>
  );
}

function metricValue(row: ReportPayload["trend"][number], metric: TrendMetric): number {
  if (metric === "sessions") return row.sessions;
  if (metric === "keyEvents") return row.keyEvents;
  if (metric === "customConversions") return row.customConversions ?? 0;
  return row.totalRevenue;
}

function buildSeries(
  trend: ReportPayload["trend"],
  selected: Set<CanonicalPlatform | typeof ALL_KEY>,
  metric: TrendMetric,
  granularity: Granularity,
  platformsPresent: CanonicalPlatform[],
): { label: string; color: string; points: Array<{ date: string; value: number }> }[] {
  const bucketize = makeBucketizer(granularity);
  const out: { label: string; color: string; points: Array<{ date: string; value: number }> }[] =
    [];

  // "All" series — sum every platform per date bucket. Rendered first
  // so it sits visually on top in chart legends; per-platform lines
  // can be layered alongside it (the user can overlay an aggregate on
  // top of selected platforms).
  if (selected.has(ALL_KEY)) {
    const byBucket = rollup(
      trend,
      (rows) => rows.reduce((acc, r) => acc + metricValue(r, metric), 0),
      (r) => bucketize(r.date),
    );
    const points = Array.from(byBucket.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, value]) => ({ date, value }));
    out.push({ label: "All social", color: ALL_COLOR, points });
  }

  // Per-platform — one series per selected platform, in the
  // session-volume order of `platformsPresent` so the chart legend
  // mirrors the chip order.
  for (const platform of platformsPresent) {
    if (!selected.has(platform)) continue;
    const rows = trend.filter((r) => r.platform === platform);
    const byBucket = rollup(
      rows,
      (xs) => xs.reduce((acc, r) => acc + metricValue(r, metric), 0),
      (r) => bucketize(r.date),
    );
    const points = Array.from(byBucket.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, value]) => ({ date, value }));
    out.push({
      label: platform,
      color: PLATFORM_COLOR_HEX[platform],
      points,
    });
  }
  return out;
}

function makeBucketizer(granularity: Granularity): (iso: string) => string {
  if (granularity === "day") return (iso) => iso;
  if (granularity === "week") {
    return (iso) => {
      const d = new Date(`${iso}T00:00:00Z`);
      const anchor = timeMonday.floor(d);
      return anchor.toISOString().slice(0, 10);
    };
  }
  return (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const anchor = timeMonth.floor(d);
    return anchor.toISOString().slice(0, 10);
  };
}
